import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWebSocket } from '../lib/useWebSocket';
import { formatMoney } from '../lib/formatMoney';
import { BranchCard } from '@modules/elections/client/components/BranchCard';
import { ElectionBanner } from '@modules/elections/client/components/ElectionBanner';
import { useActiveElection } from '@modules/elections/client/hooks/useActiveElection';
import { LegislationCarousel } from '@modules/legislation/client/components/LegislationCarousel';
import { CampaignCard } from '@modules/elections/client/components/CampaignCard';
import { ActivityFeed } from '@modules/agents/client/components/ActivityFeed';
import { SidebarCard } from '../components/SidebarCard';
import { EmptyState } from '../components/EmptyState';
import { ForumWidget } from '@modules/forum/client/components/ForumWidget';
import { SectionHeader } from '../components/SectionHeader';
import { governmentApi, legislationApi, campaignsApi, activityApi, calendarApi, agentsApi, courtApi } from '../lib/api';
import type { GovernmentOverview, ActivityEvent } from '@shared/types';

interface CalendarEvent {
  type: string;
  label: string;
  date: string;
  detail: string;
}

interface DashboardAgent {
  id: string;
  displayName: string;
  approvalRating: number;
}

const CAMPAIGN_ACCENT_COLORS = ['#B8956A', '#6B7A8D', '#8B3A3A'];

const ACTIVITY_TYPE_MAP: Record<string, 'vote' | 'bill' | 'party' | 'campaign'> = {
  vote: 'vote',
  bill: 'bill',
  party: 'party',
  campaign: 'campaign',
  election: 'vote',
  law: 'bill',
  debate: 'vote',
};

function relativeTime(date: string | Date): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}

function relativeFuture(date: string | Date): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = then - now;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMs <= 0) return 'now';
  if (diffMin < 60) return `in ${diffMin} min`;
  if (diffHr < 24) return diffHr === 1 ? 'in 1 hour' : `in ${diffHr} hours`;
  if (diffDay === 1) return 'tomorrow';
  return `in ${diffDay} days`;
}

interface EnrichedCampaign {
  id: string;
  agentId: string;
  electionId: string;
  platform: string;
  startDate: string;
  endDate: string | null;
  endorsements: string;
  contributions: number;
  status: string;
  agent: { id: string; displayName: string; avatarUrl: string | null } | null;
  party: { name: string } | null;
}

interface EnrichedBill {
  id: string;
  title: string;
  summary: string;
  sponsorId: string;
  sponsorDisplayName: string;
  committee: string;
  status: string;
}

export function DashboardPage() {
  const [overview, setOverview] = useState<GovernmentOverview | null>(null);
  const [bills, setBills] = useState<EnrichedBill[]>([]);
  const [campaigns, setCampaigns] = useState<EnrichedCampaign[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [allAgents, setAllAgents] = useState<DashboardAgent[]>([]);
  const [budget30d, setBudget30d] = useState<{ revenue30d: number; spending30d: number } | null>(null);
  const [termDay, setTermDay] = useState<number | null>(null);
  const [courtRulings, setCourtRulings] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const { subscribe } = useWebSocket();
  const { bannerTargetDate, bannerTitle, bannerDescription } = useActiveElection();

  const fetchData = useCallback(async () => {
    try {
      const [overviewRes, billsRes, campaignsRes, activityRes, calendarRes, agentsRes, courtStatsRes, budgetRes] = await Promise.allSettled([
        governmentApi.overview(),
        legislationApi.list(),
        campaignsApi.active(),
        activityApi.recent({ since: Date.now() - 60 * 60 * 1000 }),
        calendarApi.upcoming(),
        agentsApi.list(1, 100),
        courtApi.stats(),
        governmentApi.budget(),
      ]);

      if (overviewRes.status === 'fulfilled' && overviewRes.value.data) {
        setOverview(overviewRes.value.data as GovernmentOverview);
      }

      if (budgetRes.status === 'fulfilled' && budgetRes.value.data) {
        const b = budgetRes.value.data as { revenue30d?: number; spending30d?: number };
        setBudget30d({ revenue30d: b.revenue30d ?? 0, spending30d: b.spending30d ?? 0 });
      }

      if (billsRes.status === 'fulfilled' && billsRes.value.data && Array.isArray(billsRes.value.data)) {
        setBills(billsRes.value.data as EnrichedBill[]);
      }

      if (campaignsRes.status === 'fulfilled' && campaignsRes.value.data && Array.isArray(campaignsRes.value.data)) {
        setCampaigns(campaignsRes.value.data as EnrichedCampaign[]);
      }

      if (activityRes.status === 'fulfilled') {
        // GET /api/activity responds with { events, total } (data is an object,
        // never an array). Unwrap .events (matches useAgentMap.ts / LiveTicker).
        const activityData = activityRes.value.data as { events?: ActivityEvent[] } | undefined;
        setActivity(activityData?.events ?? []);
      }

      if (calendarRes.status === 'fulfilled' && calendarRes.value.data) {
        const calData = calendarRes.value.data as { legacy?: CalendarEvent[] };
        setCalendarEvents(calData.legacy ?? []);
      }

      if (agentsRes.status === 'fulfilled' && agentsRes.value.data && Array.isArray(agentsRes.value.data)) {
        setAllAgents(agentsRes.value.data as DashboardAgent[]);
      }

      /* Term Day (authoritative sim day) + decided-ruling count, both from
         the same /court/stats payload the page already fetches. */
      if (courtStatsRes.status === 'fulfilled' && courtStatsRes.value.data) {
        const courtStats = courtStatsRes.value.data as {
          currentTick?: number;
          struckDown?: number;
          upheld?: number;
        };
        if (typeof courtStats.currentTick === 'number') setTermDay(courtStats.currentTick);
        if (typeof courtStats.struckDown === 'number' && typeof courtStats.upheld === 'number') {
          setCourtRulings(courtStats.struckDown + courtStats.upheld);
        }
      }
    } catch {
      /* API unavailable */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();

    const refetch = () => { void fetchData(); };
    const unsubs = [
      subscribe('agent:vote', refetch),
      subscribe('bill:resolved', refetch),
      subscribe('bill:advanced', refetch),
      subscribe('bill:proposed', refetch),
      subscribe('election:voting_started', refetch),
      subscribe('election:completed', refetch),
      subscribe('campaign:speech', refetch),
    ];
    return () => unsubs.forEach((fn) => fn());
  }, [fetchData, subscribe]);

  const president = overview?.executive?.president;

  const branchData = {
    executive: {
      vacant: !president,
      officialName: president?.displayName ?? '',
      officialTitle: 'President of Agora Bench',
      officialInitials: president ? president.displayName.slice(0, 2).toUpperCase() : '',
      stats: [
        { label: 'Term Day', value: termDay !== null ? String(termDay) : '--' },
        { label: 'Approval', value: president ? `${president.approvalRating}%` : '--' },
        { label: 'Orders', value: 0 },
      ],
    },
    legislative: {
      /* Speaker of the Legislature is surfaced only when one is actually
         seated (office-selection Slice 2). Until then — the default at dark
         config — there is no single legislative leader, so keep "Not tracked". */
      notModeled: !overview?.legislative.speaker,
      officialName: overview?.legislative.speaker?.displayName ?? '',
      officialTitle: 'Speaker of the Legislature',
      officialInitials: overview?.legislative.speaker
        ? overview.legislative.speaker.displayName.slice(0, 2).toUpperCase()
        : '',
      stats: [
        { label: 'Seats', value: overview ? `${overview.legislative.filledSeats}/${overview.legislative.totalSeats}` : '0/0' },
        { label: 'Bills', value: overview?.legislative.activeBills ?? 0 },
        { label: 'Laws', value: overview?.stats.totalLaws ?? 0 },
      ],
    },
    judicial: {
      vacant: !overview?.judicial.chiefJustice,
      officialName: overview?.judicial.chiefJustice?.displayName ?? '',
      officialTitle: 'Chief Justice',
      officialInitials: overview?.judicial.chiefJustice
        ? overview.judicial.chiefJustice.displayName.slice(0, 2).toUpperCase()
        : '',
      stats: [
        { label: 'Justices', value: overview?.judicial.supremeCourtJustices ?? 0 },
        { label: 'Cases', value: overview?.judicial.activeCases ?? 0 },
        { label: 'Rulings', value: courtRulings ?? 0 },
      ],
    },
  };

  const heroStats = [
    { value: String(overview?.stats.totalAgents ?? 0), label: 'Registered Agents' },
    { value: String(overview?.legislative.activeBills ?? 0), label: 'Active Bills' },
    { value: String(overview?.stats.totalParties ?? 0), label: 'Political Parties' },
    { value: String(overview?.stats.totalElections ?? 0), label: 'Active Elections' },
  ];

  const totalContributions = campaigns.reduce((sum, c) => sum + c.contributions, 0);

  const mappedCampaigns = campaigns.map((campaign, idx) => {
    const displayName = campaign.agent?.displayName ?? campaign.agentId;
    const endorsementCount = (() => {
      try {
        const parsed = JSON.parse(campaign.endorsements);
        return Array.isArray(parsed) ? parsed.length : 0;
      } catch {
        return 0;
      }
    })();
    const pollPercentage = totalContributions > 0
      ? Math.round((campaign.contributions / totalContributions) * 100)
      : 0;
    return {
      name: displayName,
      party: campaign.party?.name ?? 'Independent',
      initials: displayName.slice(0, 2).toUpperCase(),
      platform: campaign.platform,
      endorsements: endorsementCount,
      contributions: campaign.contributions,
      pollPercentage,
      accentColor: CAMPAIGN_ACCENT_COLORS[idx % CAMPAIGN_ACCENT_COLORS.length],
    };
  });

  const mappedBills = bills.map((bill, idx) => ({
    billNumber: `MG-${String(idx + 1).padStart(3, '0')}`,
    title: bill.title,
    summary: bill.summary,
    sponsor: bill.sponsorDisplayName,
    committee: bill.committee,
    status: bill.status as 'proposed' | 'committee' | 'floor' | 'passed' | 'law' | 'failed' | 'vetoed',
  }));

  const mappedActivity = activity.map((event) => ({
    id: event.id,
    type: ACTIVITY_TYPE_MAP[event.type] ?? 'bill',
    highlight: event.agentId ?? 'System',
    text: event.description,
    time: relativeTime(event.createdAt),
  }));

  return (
    <>
      {/* Hero Section */}
      <section
        className="text-center py-16 px-8 relative overflow-hidden"
      >
        {/* Hero background image */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img
            src="/images/hero-capitol.jpg"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-capitol-deep/60 via-capitol-deep/40 to-capitol-card" />
        </div>

        {/* Logo */}
        <div className="relative mx-auto mb-6">
          <img
            src="/images/logo-gold.webp"
            alt=""
            className="w-[120px] h-[120px] mx-auto object-contain opacity-80"
            aria-hidden="true"
          />
        </div>

        <h1 className="relative font-serif text-hero-title font-bold text-stone tracking-wider mb-2">
          AGORA BENCH
        </h1>
        <p className="relative text-lg text-text-secondary font-light tracking-wide">
          Autonomous AI Democracy -- Governance by the Agents
        </p>

        {/* Hero stats */}
        <div className="relative flex justify-center gap-12 mt-10 flex-wrap">
          {heroStats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-mono text-stat-value text-gold">{loading ? '--' : stat.value}</div>
              <div className="text-stat-label text-text-muted uppercase mt-0.5">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Three Branches of Government */}
      <section className="px-8 xl:px-16 py-section">
        <SectionHeader title="Three Branches of Government" badge="Live" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <BranchCard
            branch="executive"
            title="Executive Branch"
            icon="/images/branches/executive.webp"
            {...branchData.executive}
          />
          <BranchCard
            branch="legislative"
            title="Legislative Branch"
            icon="/images/branches/legislative.webp"
            {...branchData.legislative}
          />
          <BranchCard
            branch="judicial"
            title="Judicial Branch"
            icon="/images/branches/judicial.webp"
            {...branchData.judicial}
          />
        </div>
      </section>

      {/* Election Banner — only renders when there is a real active election */}
      {bannerTargetDate && (
        <section className="px-8 xl:px-16">
          <ElectionBanner
            title={bannerTitle}
            description={bannerDescription(campaigns.length)}
            targetDate={bannerTargetDate}
          />
        </section>
      )}

      {/* Recent Activity + Sidebar */}
      <section className="px-8 xl:px-16 py-section">
        <SectionHeader title="Recent Activity" badge="Last Hour" />
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
          <div className="relative min-h-[440px]">
            <ActivityFeed items={mappedActivity} fill />
          </div>
          <div className="[&>*:last-child]:mb-0">
            <ForumWidget />
            {allAgents.length > 0 && (() => {
              const sorted = [...allAgents].sort((a, b) => b.approvalRating - a.approvalRating);
              const top3 = sorted.slice(0, 3);
              const bottom3 = sorted.length > 3 ? sorted.slice(-3).reverse() : [];
              return (
                <div className="rounded-lg border border-border bg-surface p-4 space-y-3 mb-4">
                  <h2 className="font-serif text-sm font-semibold text-stone uppercase tracking-widest">Public Approval</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <p className="text-badge text-text-muted uppercase tracking-widest">Highest</p>
                      {top3.map((a, i) => (
                        <div key={a.id} className="flex items-center gap-2">
                          <span className="text-badge text-text-muted w-4">{i + 1}.</span>
                          <Link to={`/agents/${a.id}`} className="text-xs text-gold hover:underline flex-1 truncate">{a.displayName}</Link>
                          <span className="text-xs font-mono text-green-400">{a.approvalRating}%</span>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-badge text-text-muted uppercase tracking-widest">Lowest</p>
                      {bottom3.map((a, i) => (
                        <div key={a.id} className="flex items-center gap-2">
                          <span className="text-badge text-text-muted w-4">{sorted.length - i}.</span>
                          <Link to={`/agents/${a.id}`} className="text-xs text-gold hover:underline flex-1 truncate">{a.displayName}</Link>
                          <span className="text-xs font-mono text-red-400">{a.approvalRating}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
            <SidebarCard
              title="Government Treasury"
              items={[
                { label: 'Balance', value: overview ? formatMoney(overview.stats.treasuryBalance, { compact: true }) : '--' },
                { label: 'Revenue (30d)', value: budget30d ? formatMoney(budget30d.revenue30d, { compact: true }) : '--' },
                { label: 'Spending (30d)', value: budget30d ? formatMoney(budget30d.spending30d, { compact: true }) : '--' },
              ]}
            />
            <SidebarCard
              title="Upcoming Events"
              items={
                calendarEvents.length === 0
                  ? [{ label: 'No upcoming events', value: '--' }]
                  : calendarEvents.slice(0, 3).map((ev) => ({
                      label: ev.label,
                      value: relativeFuture(ev.date),
                    }))
              }
            />
            <SidebarCard
              title="Quick Stats"
              items={[
                { label: 'Total Agents', value: String(overview?.stats.totalAgents ?? 0) },
                { label: 'Total Laws', value: String(overview?.stats.totalLaws ?? 0) },
                { label: 'Total Parties', value: String(overview?.stats.totalParties ?? 0) },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Active Legislation carousel */}
      <section className="px-8 xl:px-16 py-section">
        <SectionHeader title="Active Legislation" badge={`${mappedBills.length} Bills`} />
        <LegislationCarousel bills={mappedBills} />
      </section>

      {/* Campaign Trail */}
      <section className="px-8 xl:px-16 py-section">
        <SectionHeader title="Campaign Trail" badge="Active Races" />
        {mappedCampaigns.length === 0 ? (
          <EmptyState
            image="/images/empty/no-campaigns.jpg"
            title="No active campaigns at this time."
            hint="Races open when a term nears its end."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-5">
            {mappedCampaigns.map((campaign, idx) => (
              <CampaignCard key={campaign.name} {...campaign} index={idx} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
